import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { Layout } from "../components/Layout";

const SECTIONS = [
  ["Our promise", [
    "Every curio in the cabinet should be true, clearly explained and traceable to sources you can check yourself. When a claim is uncertain or disputed, we say so rather than dressing it up as fact.",
  ]],
  ["How we choose curios", [
    "We look for facts that are surprising but well documented: natural phenomena, forgotten history, the human body, science and the wider universe. Popular internet 'facts' that turn out to be myths are either rewritten as debunked legends or left out.",
  ]],
  ["Sourcing", [
    "Each curio cites its sources at the foot of the page. We favour peer-reviewed research, museums, universities, government science agencies and established reference works, and we use Wikipedia as a reader-friendly companion source rather than the only evidence.",
    "Search-engine result pages are never accepted as sources. Every cited link is checked automatically to confirm it is live. Some publishers block automated checks, so a curio is marked fact-checked once at least one of its sources has been confirmed live and supports the claim.",
  ]],
  ["Fact-checking", [
    "Before publishing, the curator checks every name, number and date against the cited sources. Where sources disagree we follow the most authoritative one and soften the wording. Curios that cannot be verified stay unpublished.",
    "We use AI tools to help draft longer explanations, suggest additional sources and illustrate curios. AI output is never published unreviewed: drafts are grounded only in the cited sources and approved line by line by a human curator.",
  ]],
  ["Illustrations", [
    "Curio illustrations are AI-generated engravings in the style of antique natural-history plates. They are decorative interpretations, not photographs or scientific diagrams.",
  ]],
  ["Corrections", [
    "If you spot an error, please tell us through the Contact page with a link to a reliable source. We review every report, correct confirmed errors promptly, and update the 'Last updated' date on the curio. Significant corrections are noted on the page.",
  ]],
  ["Trivia Vault", [
    "Quiz questions follow the same standards. Each question has a single defensible answer and a fun fact drawn from a cited source. Reworded duplicates are merged so every question tests a different fact.",
  ]],
  ["Independence", [
    "CurioVault is supported by advertising and Club memberships. Advertisers have no influence over which curios we publish or what they say.",
  ]],
];

const EditorialStandards = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Editorial Standards · CurioVault";
    return () => { document.title = "CurioVault"; };
  }, []);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="editorial-standards-page">
        <nav className="flex items-center gap-2 font-mono-cv text-xs text-[#7A6C5D] mb-8" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#9E4535]">Vault</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1615]">Editorial Standards</span>
        </nav>
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 shrink-0 rounded-full border-2 border-[#C5A059] flex items-center justify-center text-[#9E4535]">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1615] leading-tight">Editorial Standards</h1>
        </div>
        <p className="caption-label mb-8 ml-16">How we source, check and correct every curio</p>

        <div className="bg-white border border-[#D8CEB9] rounded-sm p-7 sm:p-9 space-y-8">
          {SECTIONS.map(([h, ps]) => (
            <section key={h} data-testid={`standards-${h.toLowerCase().replace(/\s+/g, "-")}`}>
              <h2 className="font-serif-display text-xl sm:text-2xl font-bold text-[#1A1615] mb-3">{h}</h2>
              {ps.map((p, i) => <p key={i} className="font-body text-base sm:text-lg text-[#2E282A] leading-[1.85] mb-3 last:mb-0">{p}</p>)}
            </section>
          ))}
          <p className="font-ui text-sm text-[#7A6C5D] border-t border-[#E9E1D0] pt-5">
            Found a mistake? <Link to="/contact" data-testid="standards-contact-link" className="text-[#9E4535] font-semibold hover:underline">Send us a correction</Link>.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default EditorialStandards;
